import React, { useContext, useState } from 'react'

// @mui core components
import { Box, Grid, Typography } from '@mui/material'
import Card from 'atoms/Card'

// Data
import { IntakeContext } from 'context/IntakeContext'
import { selectData } from 'layouts/onboarding'

// Popup
import { toast } from 'react-toastify'
import { renderMuiFormField } from 'utils/forms/renderMuiFormField'

// Debounce
import { useFormDebounce } from 'hooks/useFormDebounce'
import OnboardingMultiSelectTile from 'components/OnboardingMultiSelectTile'
import LoadingButton from 'components/LoadingButton'
import PrivateLifeInsurance from 'admin/components/PrivateLifeInsurance'
import LifeInsuranceAssets from 'components/LifeInsuranceList'

function LifeInsurance() {
  const { intakeState, updateIntakeStateAtKey, updateUser } =
    useContext(IntakeContext)

  const { updateIntakeStateAndSubmit, loading } = useFormDebounce()
  const isGovernmentEmployee = intakeState?.governmentEmployee
  // add an onSubmit button and call the context function `updateUser`
  // const onSubmit = () => updateUser()
  const onSubmit = async () => {
    await updateUser()
    toast.info('You have successfully updated your life insurance details', {
      position: 'top-center',
      autoClose: 5000,
      pauseOnFocusLoss: false,
      pauseOnHover: false,
    })
  }
  const [localForm, setLocalForm] = useState({
    fegliOptionalValues: {
      fegliOptionA: intakeState.fegliOptionA,
      fegliOptionB: intakeState.fegliOptionB,
      fegliOptionC: intakeState.fegliOptionC,
    },
    fegliOptionBMultiples: intakeState.fegliOptionBMultiples || '',
    fegliOptionCMultiples: intakeState.fegliOptionCMultiples || '',
  })
  const handleChange = (field) => (value) => {
    setLocalForm((prev) => ({ ...prev, [field]: value }))
  }
  const showFEGLI = intakeState?.fegli
  const lifeInsuranceOther = intakeState?.lifeInsuranceOther

  return (
    <>
      <Box
        pl={3}
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Typography display="flex" align="left" variant="body2FunHeader">
          {isGovernmentEmployee ? 'FEGLI & Life Insurance' : 'Life Insurance'}
        </Typography>

        <LoadingButton loading={loading} onClick={onSubmit} />
      </Box>
      <Card>
        <Box component="form" p={3}>
          <Grid container columnSpacing={3} rowSpacing={2}>
            {isGovernmentEmployee && (
              <>
                <Grid item xs={12} sm={6}>
                  {renderMuiFormField({
                    autoFocus: false,
                    id: 'fegli',
                    label: 'Are you currently enrolled in FEGLI coverage?',
                    placeholder: '',
                    inputType: 'select',
                    value: intakeState.fegli,
                    onChange: updateIntakeStateAndSubmit((fegli) =>
                      updateIntakeStateAtKey({ fegli })
                    ),
                    options: selectData.yesNo,
                  })}
                </Grid>
                {showFEGLI && (
                  <>
                    <Grid item xs={12} sm={12}>
                      <Typography
                        component="label"
                        variant="body2"
                        fontWeight="regular"
                        textTransform="capitalize"
                      >
                        Do you have any optional FEGLI coverage?
                      </Typography>

                      {/* this currently does not debounce properly, but does update the intakeState */}
                      <OnboardingMultiSelectTile
                        header={false}
                        helpText={'test'}
                        subHeader={false}
                        tileSize={{ xs: '4', sm: '4', md: '4', lg: '4' }}
                        questionArray={[
                          {
                            id: 'fegliOptionA',
                            optionLabel: 'Option A',
                          },
                          {
                            id: 'fegliOptionB',
                            optionLabel: 'Option B',
                          },
                          {
                            id: 'fegliOptionC',
                            optionLabel: 'Option C',
                          },
                        ]}
                      />

                      {/* {renderMuiFormField({
                    autoFocus: false,
                    id: 'optionalFegli',
                    label: 'Optional FEGLI Coverage',
                    placeholder: '0',
                    inputType: 'multiselect',
                    value: localForm.fegliOptionalValues,
                    onChange: (e) => handleChange('fegliOptionalValues')(e),
                    // onBlur: updateIntakeStateAndSubmit(
                    //   (fegliOptionBMultiples) =>
                    //     updateIntakeStateAtKey({ fegliOptionBMultiples })
                    // ),
                  })} */}
                    </Grid>

                    {intakeState.fegliOptionB && (
                      <Grid item xs={12} sm={intakeState.fegliOptionC ? 6 : 12}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'fegliOptionBMultiples',
                          label: 'How many multiples of option B do you have?',
                          placeholder: '0',
                          inputType: 'int',
                          maxValue: 5,
                          value: localForm.fegliOptionBMultiples,
                          onChange: (e) =>
                            handleChange('fegliOptionBMultiples')(e),
                          onBlur: updateIntakeStateAndSubmit(
                            (fegliOptionBMultiples) =>
                              updateIntakeStateAtKey({ fegliOptionBMultiples })
                          ),
                        })}
                      </Grid>
                    )}
                    {intakeState.fegliOptionC && (
                      <Grid item xs={12} sm={intakeState.fegliOptionB ? 6 : 12}>
                        {renderMuiFormField({
                          autoFocus: false,
                          id: 'fegliOptionCMultiples',
                          label: 'How many multiples of option C do you have?',
                          placeholder: '0',
                          inputType: 'int',
                          maxValue: 5,
                          value: localForm.fegliOptionCMultiples,
                          onChange: (e) =>
                            handleChange('fegliOptionCMultiples')(e),
                          onBlur: updateIntakeStateAndSubmit(
                            (fegliOptionCMultiples) =>
                              updateIntakeStateAtKey({ fegliOptionCMultiples })
                          ),
                        })}
                      </Grid>
                    )}
                  </>
                )}
              </>
            )}
            <Grid item xs={12} mt={2}>
              <Typography variant="h5">Private Life Insurance</Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              {renderMuiFormField({
                autoFocus: false,
                id: 'lifeInsuranceOther',
                label: 'Do you have any private life insurance?',
                placeholder: '',
                inputType: 'select',
                value: intakeState.lifeInsuranceOther,
                onChange: updateIntakeStateAndSubmit((lifeInsuranceOther) =>
                  updateIntakeStateAtKey({ lifeInsuranceOther })
                ),
                options: selectData.yesNo,
              })}
            </Grid>
            {lifeInsuranceOther && (
              <Grid item xs={12}>
                <LifeInsuranceAssets demoMode={false} />
                {/* <PrivateLifeInsurance
                  numProducts={
                    intakeState?.privateLifeInsurance
                      ? intakeState.privateLifeInsurance.length
                      : 0
                  }
                  initialProducts={intakeState?.privateLifeInsurance || []}
                  updateIntakeStateAndSubmit={updateIntakeStateAndSubmit}
                  updateIntakeStateAtKey={updateIntakeStateAtKey}
                /> */}
              </Grid>
            )}
          </Grid>
        </Box>
      </Card>
    </>
  )
}

export default React.memo(LifeInsurance)
